import React, { FC, useEffect, useState, startTransition } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import FullPageMessage from 'src/view/components/FullPageMessage';
import FullPageLoader from 'src/view/components/FullPageLoader';
import { Home } from '@mui/icons-material';
import clientService from 'src/clientService';

interface ProjectDetailsFetcherWrapperProps {
  children: React.ReactNode;
}

const ProjectDetailsFetcherWrapper: FC<ProjectDetailsFetcherWrapperProps> = ({ children }) => {
  const { id, siteId } = useParams<{ id: string; siteId: string }>();
  // const location = useLocation();
  const { state: appState, dispatch } = useAppState();
  const { selectedProject } = appState;
  const { showSnackbar, snackbarType } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setError(null);

      Promise.all([
        clientService.api.readProjectApiV1ProjectsProjectIdGet(id, { get_satellite_images: false }),
        clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(id),
      ])
        .then(([projectRes, sitesRes]) => {
          startTransition(() => {
            dispatch({
              type: 'SET_PROJECT_DETAILS',
              payload: {
                ...projectRes.data,
                sites: sitesRes.data,
              },
            });
            setIsLoading(false);
          });
        })
        .catch((apiError) => {
          console.error('Failed to fetch project details:', apiError);
          const errorMessage =
            apiError.response?.data?.detail || `Failed to load project details for ID: ${id}.`;
          startTransition(() => {
            setError(errorMessage);
            showSnackbar({ message: 'Error fetching project details.', type: snackbarType.ERROR });
            setIsLoading(false);
          });
        });
    } else {
      startTransition(() => {
        setError('The project ID is missing in the route parameters.');
        showSnackbar({ message: 'The project ID is missing.', type: snackbarType.ERROR });
        setIsLoading(false);
      });
    }
  }, [id]);

  useEffect(() => {
    if (siteId && selectedProject?.projectDetails) {
      startTransition(() => {
        const site = selectedProject.projectDetails?.sites?.find((site) => site.site_id === siteId);
        if (site) {
          dispatch({
            type: 'SET_SELECTED_SITE',
            payload: site,
          });
        }
      });
    }
  }, [siteId, selectedProject?.projectDetails]);

  if (isLoading) {
    return (
      <FullPageLoader
        title="Just a moment, crafting your project view..."
        message="Almost there..."
      />
    );
  }

  if (error) {
    return (
      <FullPageMessage
        headline="Uh-oh! The project data took an unexpected detour."
        subHeadline={`We're having a little trouble loading the details. The system reports: ${error}`}
        ctaText="Go to Home"
        onCtaClick={() => {
          window.location.href = '/';
        }}
        ctaIcon={<Home />}
      />
    );
  }

  return <>{children}</>;
};

export default ProjectDetailsFetcherWrapper;
